import {
  Box,
  Chip,
  TextField,
} from "@mui/material";
import React, { useState, useMemo } from "react";
import ReusableDataTable from "../../Component/DataTable/ReusableDataTablePrime";
import { FilterMatchMode } from "primereact/api";
import { useDispatch, useSelector } from "react-redux";
import { setLoading } from "../../store/CommonSlice";
import { CustomMessage } from "../../const/Spinner";
import { enqueueSnackbar } from "notistack";
import ReusablePrimeButton from "../../Component/Buttons/ReusablePrimeButton";
import AddIcon from "@mui/icons-material/Add";
import { InspectionTypeType } from "../../types/assetTypes";
import { getInpectionTypeColumns } from "../../const/AssertData";
import {
  getInspectionTypeById,
  getInspectionTypeList,
  saveInspectionType,
} from "../../api/Services/masterServie";
import ReusablePrimeReactDialog from "../../Component/Dialog/ReusablePrimeReactDialog";
import { RootState } from "../../store/Reduxstore";

interface InspectionTypeProps {
  setPathname: (path: string) => void;
}

const InspectionType: React.FC = () => {
  const dispatch = useDispatch();
  const [data, setData] = useState<InspectionTypeType[]>([]);
  const [pagination, setPagination] = useState({
    page: 1,
    rows: 13,
    totalRecords: 0,
  });
  const [openDialog, setOpenDialog] = useState(false);
  const [inspectionTypeData, setInspectionTypeData] = useState<InspectionTypeType>({
    inspectionTypeId: 0,
    inspectionTypeName: "",

  });

  const columns = useMemo(() => getInpectionTypeColumns(), []);

  if (columns.filter((val) => val.field === "action").length === 0) {
    columns.push({
      field: "action",
      header: "Action",
      sortable: false,
      filter: false,
      filterField: "action",
      filterPlaceholder: "Search",
      style: { minWidth: "12rem" },
      body: (rowData: any) => (
        <ReusablePrimeButton
          icon="pi pi-pencil"
          className="p-button-rounded p-button-outlined"
          onClick={() => handleEditInspectionType(rowData.inspectionTypeId)}
        />
      ),
    });
  }

  const [filterData, setFilterData] = useState<Record<string, any>>(() =>
    (columns ?? []).reduce((acc, col) => {
      if (col.field) {
        acc[col.field] = { value: "", matchMode: "startsWith" };
      }
      return acc;
    }, {} as Record<string, { value: string; matchMode: string }>),
  );

  const handleEditInspectionType = async (inspectionTypeId: number) => {
    dispatch(setLoading(true));
    try {
      const response = await getInspectionTypeById(inspectionTypeId);
      let { data, status } = response;
      if (status.code === 200 && data) {
        setInspectionTypeData(data);
        setOpenDialog(true);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const filters = useMemo(() => {
    return columns.reduce(
      (acc, col) => {
        if (col.field) {
          acc[col.field] = {
            value: null,
            matchMode: FilterMatchMode.STARTS_WITH,
          };
        }
        return acc;
      },
      {} as Record<string, { value: any; matchMode: FilterMatchMode }>
    );
  }, [columns]);

  const onFetchData = async (page: number, rows: number, filter?: Record<string, any>) => {
    dispatch(setLoading(true));
    try {
      const { data, status, pagination } = await getInspectionTypeList(page, rows, filter ?? filterData);

      if (status.code === 200 && data?.length > 0) {
        setData(data);
        setPagination({
          page: pagination.currentPage,
          rows: pagination.limit,
          totalRecords: pagination.totalCount,
        });
      } else {
        setData([]);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setInspectionTypeData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleClear = () => {
    setOpenDialog(false);
    setInspectionTypeData({
      inspectionTypeId: 0,
      inspectionTypeName: "",

    });
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event?.preventDefault();
    if (inspectionTypeData.inspectionTypeName === "") {
      CustomMessage("Add the inspectionType name", "error", enqueueSnackbar);
      return;
    }
    dispatch(setLoading(true));
    try {
      const response = await saveInspectionType(inspectionTypeData);
      let { status } = response;
      if (status.code === 200 || status.code === 201) {
        handleClear();
        onFetchData(PAGESTART, PAGESEND, filterData);
      } else {
        CustomMessage(status.message, "error", enqueueSnackbar);
      }
    } catch (error: any) {
      CustomMessage(error?.message, "error", enqueueSnackbar);
    } finally {
      dispatch(setLoading(false));
    }
  };
  const userDetails = useSelector((state: RootState) => state.auth)

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center", // Aligns vertically in the center
          mb: 2,
        }}
      >
        <h2
          style={{
            margin: "0 0 12px 8px",
            fontWeight: 500,
            fontSize: "18px",
          }}
        >
          Inspection Type
        </h2>

        {userDetails.role.some(
          r => r.roleName === "Admin" || r.roleName === "Inpection Type Write"
        ) && (
            <div>
              <Chip
                className="btn-black-outlined mb-3"
                label="New"
                color="warning"
                onClick={() => setOpenDialog(true)}
                icon={<AddIcon />}
                variant="outlined"
              />
            </div>
          )}

      </Box>
      <Box
        sx={{
          backgroundColor: "#f9f9f9",
          borderRadius: 3,
          boxShadow: 3,
          padding: 2,
        }}
      >
        <ReusableDataTable
          data={data}
          columns={columns}
          onFetchData={onFetchData}
          emptyMessage="No data found."
          paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
          selectionMode="multiple"
          rowsPerPageOptions={[5, 10, 25, 50]}
          pagination={pagination}
          setPagination={setPagination}
          filters={filterData}
          setFilters={setFilterData}
          onRowDoubleClick={(rowData) => {
            console.log('Double-click row:', rowData);
            handleEditInspectionType(rowData.inspectionTypeId);
          }}
        />
      </Box>
      <ReusablePrimeReactDialog
        header="Add InspectionType"
        visible={openDialog}
        onHide={handleClear}
      >
        <form
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
            }
          }}
        >
          <div className="mb-3">
            <h6>InspectionType Name</h6>
            <TextField
              label=""
              fullWidth
              variant="outlined"
              value={inspectionTypeData.inspectionTypeName}
              name="inspectionTypeName"
              onChange={handleChange}
              sx={{ mt: 0 }}
              required
            />
          </div>

          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            marginTop: "3px",
            gap: 3
          }}>
            <ReusablePrimeButton
              onClick={handleClear}
              severity="contrast"
              title="Cancel"
              type="button"
            >
              Cancel
            </ReusablePrimeButton>
            {userDetails.role.some(
              (r) => r.roleName === "Admin" || r.roleName === "Inspection Type Write"
            ) && (
                <ReusablePrimeButton
                  className="ms-1"
                  severity="info"
                  onClick={handleSubmit}
                >
                  Submit
                </ReusablePrimeButton>
              )}
          </div>
        </form>
      </ReusablePrimeReactDialog>
    </>
  );
};

export default InspectionType;
